/**
 * Unified Theme System - Single Source of Truth
 *
 * Cloud-inspired design system for cloudcard NFC shop
 * Professional, modern, technical aesthetic
 *
 * Usage:
 *   import { theme } from './theme';
 *   style={{ color: theme.colors.accent }}
 */

export const theme = {
  // Color Palette
  colors: {
    // Backgrounds
    bg: '#f8fafc',           // Light gray background
    bgAlt: '#f1f5f9',        // Alternate background (slightly darker)
    surface: '#ffffff',       // Card/panel surfaces
    surfaceHover: '#e2e8f0',  // Hover state for surfaces

    // Accents
    accent: '#0ea5e9',        // Primary accent (sky blue)
    accentAlt: '#3b82f6',     // Secondary accent (blue)
    accentHover: '#0284c7',   // Darker hover state

    // Text
    text: '#0f172a',          // Primary text (almost black)
    textMuted: '#64748b',     // Muted/secondary text (gray)

    // Borders
    border: '#e2e8f0',        // Default border color
    borderHover: '#cbd5e1',   // Hover state border

    // Gradients
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)',
    gradientHover: 'linear-gradient(135deg, #0284c7 0%, #2563eb 100%)',

    // Semantic Colors
    error: '#ef4444',         // Error state (red)
    errorBg: '#fee2e2',       // Error background
    errorBorder: '#fecaca',   // Error border
    errorText: '#991b1b',     // Error text (dark red)

    success: '#10b981',       // Success state (green)
    successBg: '#dcfce7',     // Success background
    successBorder: '#bbf7d0', // Success border
    successText: '#14532d',   // Success text (dark green)

    info: '#3b82f6',          // Info state (blue)
    infoBg: '#dbeafe',        // Info background
    infoBorder: '#bfdbfe',    // Info border
    infoText: '#1e3a8a',      // Info text (dark blue)

    warning: '#f59e0b',       // Warning state (amber)
    warningBg: '#fef3c7',     // Warning background
    warningBorder: '#fde68a', // Warning border
    warningText: '#78350f',   // Warning text (dark amber)
  },

  // Typography
  fonts: {
    display: "'Syne', sans-serif",           // Headings, emphasis
    body: "'DM Sans', sans-serif",           // Body text, UI
    mono: "'JetBrains Mono', monospace",     // Code, labels, tags
  },

  // Font Weights
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  // Font Sizes
  fontSizes: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '32px',
    '4xl': '40px',
    '5xl': '48px',
    '6xl': '64px',
  },

  // Line Heights
  lineHeights: {
    tight: 1.2,
    snug: 1.4,
    normal: 1.5,
    relaxed: 1.6,
    loose: 1.8,
  },

  // Letter Spacing
  letterSpacing: {
    tight: '-1px',
    normal: '0px',
    wide: '1px',
    wider: '2px',
  },

  // Spacing Scale (8px base)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '40px',
    '3xl': '48px',
    '4xl': '64px',
    '5xl': '80px',
    '6xl': '100px',
  },

  // Border Radius
  radius: {
    sm: '8px',      // Small elements (pills, badges)
    md: '12px',     // Buttons, inputs
    lg: '16px',     // Cards
    xl: '20px',     // Large cards
    '2xl': '24px',  // Modals, sections
    full: '9999px', // Fully rounded (circles, pills)
  },

  // Shadows
  shadows: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
    md: '0 4px 12px rgba(0, 0, 0, 0.04)',
    lg: '0 8px 24px rgba(0, 0, 0, 0.08)',
    xl: '0 12px 40px rgba(0, 0, 0, 0.12)',
    '2xl': '0 20px 60px rgba(0, 0, 0, 0.15)',
    accent: '0 4px 20px rgba(14, 165, 233, 0.2)',
    accentHover: '0 8px 30px rgba(14, 165, 233, 0.3)',
  },

  // Animation Timing
  timing: {
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '400ms',
  },

  // Easing Curves
  easing: {
    smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',      // Ease-out with slight overshoot
    bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',  // Bounce effect
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',        // Standard ease-in-out
    linear: 'linear',
  },

  // Z-Index Scale
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1100,
    overlay: 9000,
    modal: 9500,
    toast: 9999,
  },

  // Breakpoints (for documentation - use in media queries)
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

/**
 * Helper function to create responsive clamp values
 * @param {string} min - Minimum size (e.g., '16px')
 * @param {string} preferred - Preferred size (e.g., '4vw')
 * @param {string} max - Maximum size (e.g., '24px')
 */
export const clamp = (min, preferred, max) =>
  `clamp(${min}, ${preferred}, ${max})`;

/**
 * Helper to get semantic color with opacity
 * @param {string} color - Hex color
 * @param {number} opacity - Opacity 0-1
 */
export const rgba = (color, opacity) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Premium Theme Variant
 * Luxurious, sophisticated aesthetic for premium.cloudcarded.com
 */
export const premiumTheme = {
  ...theme,
  colors: {
    // Backgrounds - Darker, more luxurious
    bg: '#0a0a0a',           // Deep black background
    bgAlt: '#141414',        // Slightly lighter black
    surface: '#1a1a1a',       // Dark gray surfaces
    surfaceHover: '#242424',  // Hover state for surfaces

    // Accents - Gold and premium
    accent: '#d4af37',        // Luxury gold
    accentAlt: '#f4c542',     // Brighter gold
    accentHover: '#b8941f',   // Darker gold hover

    // Text - Light on dark
    text: '#f8fafc',          // Almost white text
    textMuted: '#94a3b8',     // Muted gray text

    // Borders - Subtle luxury
    border: '#2a2a2a',        // Dark border
    borderHover: '#3a3a3a',   // Lighter hover border

    // Gradients - Premium gold to amber
    gradient: 'linear-gradient(135deg, #d4af37 0%, #f59e0b 100%)',
    gradientHover: 'linear-gradient(135deg, #b8941f 0%, #d97706 100%)',

    // Semantic Colors (adjusted for dark theme)
    error: '#ef4444',
    errorBg: '#3f1515',
    errorBorder: '#5f1e1e',
    errorText: '#fca5a5',

    success: '#10b981',
    successBg: '#14342b',
    successBorder: '#1e4f41',
    successText: '#6ee7b7',

    info: '#3b82f6',
    infoBg: '#1e2a47',
    infoBorder: '#2d3f66',
    infoText: '#93c5fd',

    warning: '#f59e0b',
    warningBg: '#3f2915',
    warningBorder: '#5f3e1e',
    warningText: '#fbbf24',
  },
  shadows: {
    ...theme.shadows,
    accent: '0 4px 20px rgba(212, 175, 55, 0.3)',
    accentHover: '0 8px 30px rgba(212, 175, 55, 0.4)',
  },
};

/**
 * Get theme based on subdomain
 * @param {string|null} subdomain - Current subdomain
 * @returns {object} Theme object
 */
export const getTheme = (subdomain) => {
  if (subdomain === 'premium') {
    return premiumTheme;
  }
  return theme;
};

export default theme;
